import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { User, Shield, ChevronDown, ArrowLeftRight, Thermometer, BookOpen, ExternalLink, Image, KeyRound } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useActiveCompetition } from '../../hooks/useActiveCompetition'
import { useTeamFeature } from '../../hooks/useTeamFeature'
import { LogoutButton } from '../index'
import { MobileNav } from './MobileNav'
import albatrossIcon from '../../assets/albatross-icon.svg'
import { config } from '../../config/environment'

interface AppHeaderProps {
  activePage?: 'dashboard' | 'events' | 'photos' | 'membership' | 'admin' | 'head-to-head' | 'thermal' | 'coaching-notes' | 'team'
}

const ALBATROSS_PAGES = ['head-to-head', 'thermal', 'coaching-notes'] as const

export function AppHeader({ activePage }: AppHeaderProps) {
  const { user } = useAuth()
  const { albatrossCompId } = useActiveCompetition()
  const { enabled: teamEnabled } = useTeamFeature()
  const [albatrossOpen, setAlbatrossOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const isActive = (page: string) => activePage === page
  const isAlbatrossActive = ALBATROSS_PAGES.includes(activePage as typeof ALBATROSS_PAGES[number])

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setAlbatrossOpen(false)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const albatrossBaseUrl = config.albatrossUrl.replace(/\/api\/?$/, '')
  const fullAnalysisUrl = albatrossCompId
    ? `${albatrossBaseUrl}/competitions/${albatrossCompId}`
    : albatrossBaseUrl

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3">
            <img
              src="/fwo-logo.png"
              alt="FlyWideOpen"
              className="h-10"
            />
            <h1 className="text-xl font-bold text-gray-900">Pilot Portal</h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8 items-center">
            <Link
              to="/dashboard"
              className={isActive('dashboard') ? 'text-fwo-blue font-medium' : 'text-gray-600 hover:text-fwo-blue transition-colors'}
            >
              Dashboard
            </Link>
            <Link
              to="/events"
              className={isActive('events') ? 'text-fwo-blue font-medium' : 'text-gray-600 hover:text-fwo-blue transition-colors'}
            >
              Events
            </Link>
            <Link
              to="/photos"
              className={isActive('photos') ? 'text-fwo-blue font-medium' : 'text-gray-600 hover:text-fwo-blue transition-colors'}
            >
              Photos
            </Link>
            {config.galleryUrl && (
              <a
                href={config.galleryUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-gray-600 hover:text-fwo-blue transition-colors"
              >
                <Image className="h-4 w-4" />
                Gallery
                <ExternalLink className="h-3 w-3 text-gray-400" />
              </a>
            )}
            <Link
              to="/membership"
              className={isActive('membership') ? 'text-fwo-blue font-medium' : 'text-gray-600 hover:text-fwo-blue transition-colors'}
            >
              Pilot ID Card
            </Link>

            {teamEnabled && (
              <Link
                to="/team"
                className={isActive('team') ? 'text-fwo-blue font-medium' : 'text-gray-600 hover:text-fwo-blue transition-colors'}
              >
                Team
              </Link>
            )}

            {/* Albatross Analysis Dropdown */}
            {albatrossCompId && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setAlbatrossOpen((o) => !o)}
                  className={`flex items-center gap-1 transition-colors ${
                    isAlbatrossActive
                      ? 'text-fwo-blue font-medium'
                      : 'text-gray-600 hover:text-fwo-blue'
                  }`}
                >
                  <img src={albatrossIcon} alt="" className="h-4 w-4" />
                  Albatross Analysis
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${albatrossOpen ? 'rotate-180' : ''}`} />
                </button>

                {albatrossOpen && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <a
                      href={fullAnalysisUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setAlbatrossOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-fwo-blue"
                    >
                      <ExternalLink className="h-4 w-4 text-gray-400" />
                      Full Analysis
                    </a>
                    <Link
                      to="/coaching-notes"
                      onClick={() => setAlbatrossOpen(false)}
                      className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 hover:text-fwo-blue ${
                        isActive('coaching-notes') ? 'text-fwo-blue font-medium bg-blue-50' : 'text-gray-700'
                      }`}
                    >
                      <BookOpen className="h-4 w-4 text-gray-400" />
                      Coaching Notes
                    </Link>
                    <Link
                      to="/head-to-head"
                      onClick={() => setAlbatrossOpen(false)}
                      className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 hover:text-fwo-blue ${
                        isActive('head-to-head') ? 'text-fwo-blue font-medium bg-blue-50' : 'text-gray-700'
                      }`}
                    >
                      <ArrowLeftRight className="h-4 w-4 text-gray-400" />
                      Head-to-Head
                    </Link>
                    <Link
                      to="/thermal-map"
                      onClick={() => setAlbatrossOpen(false)}
                      className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 hover:text-fwo-blue ${
                        isActive('thermal') ? 'text-fwo-blue font-medium bg-blue-50' : 'text-gray-700'
                      }`}
                    >
                      <Thermometer className="h-4 w-4 text-gray-400" />
                      Thermal Map
                    </Link>
                  </div>
                )}
              </div>
            )}

            {user && (user.role === 'admin' || user.role === 'photo_admin') && (
              <Link
                to="/admin"
                className={isActive('admin') ? 'text-fwo-blue font-medium flex items-center gap-1' : 'text-gray-600 hover:text-fwo-blue transition-colors flex items-center gap-1'}
              >
                <Shield className="h-4 w-4" />Admin
              </Link>
            )}
          </nav>

          {/* Desktop User Info & Logout */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((o) => !o)}
                className="flex items-center space-x-2 text-sm text-gray-600 hover:text-fwo-blue transition-colors cursor-pointer"
              >
                <User className="h-4 w-4" />
                <span>{user?.firstName} {user?.lastName}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {userMenuOpen && (
                <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <Link
                    to="/change-password"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-fwo-blue"
                  >
                    <KeyRound className="h-4 w-4 text-gray-400" />
                    Change Password
                  </Link>
                </div>
              )}
            </div>
            <LogoutButton />
          </div>

          {/* Mobile Navigation */}
          <MobileNav />
        </div>
      </div>
    </header>
  )
}
